import React from 'react';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { brand } from '../theme/brand';

interface AuthCardProps {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}

/**
 * Full-height, vertically-centered authentication card used by every auth
 * screen (sign in / register / confirm / MFA / reset). A soft branded backdrop
 * sits behind a single centered card; the product lockup (mark + wordmark) is
 * above the card, the title/subtitle head the card, and a small footer line
 * sits beneath it. The product name (Supplier Gateway) is shown here rather
 * than the org wordmark (Tech Supply Chain) used in the signed-in masthead.
 */
export default function AuthCard({ title, subtitle, children }: AuthCardProps) {
    return (
        <div
            style={{
                minHeight: 'calc(100vh - 56px)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                // Sit near the top (upper area) rather than dead-center.
                padding: '56px 16px 48px',
                background: `radial-gradient(1200px 600px at 50% -10%, ${brand.navy}14, transparent 60%)`,
            }}
        >
            <div style={{ width: '100%', maxWidth: 440 }}>
                <SpaceBetween size="l">
                    {/* Product lockup: mark chip + wordmark, centered above the card. */}
                    <Box textAlign="center">
                        <SpaceBetween size="xs" alignItems="center">
                            <span
                                aria-hidden="true"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: brand.navy,
                                    color: '#fff',
                                    fontWeight: 800,
                                    fontSize: 22,
                                    letterSpacing: 0.5,
                                }}
                            >
                                SG
                            </span>
                            <span
                                style={{
                                    fontWeight: 800,
                                    fontSize: 24,
                                    letterSpacing: 0.3,
                                    color: brand.navy,
                                }}
                            >
                                Supplier Gateway
                            </span>
                        </SpaceBetween>
                    </Box>

                    <Container
                        header={
                            <Header variant="h2" description={subtitle}>
                                {title}
                            </Header>
                        }
                    >
                        {children}
                    </Container>

                    <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
                        Tech Supply Chain
                    </Box>
                </SpaceBetween>
            </div>
        </div>
    );
}

AuthCard.defaultProps = {
    subtitle: undefined,
};
